import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Chip,
  IconButton,
  Button,
  Alert,
  Tooltip,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Visibility as ViewIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { isOtherCustomer } from '../utils/auth';
import { FunctionPerformance } from '../components/performance/FunctionPerformance';
import { TargetTests } from '../components/tests/TargetTests';
import { useTestsAccess } from '../services/testsApi';
import { QuickLinks } from '../components/performance/QuickLinks';
import { PageBreadcrumbs } from '../components/PageBreadcrumbs';
import { RelativeTime } from '../components/RelativeTime';
import { usePageTitle } from '../utils/pageTitle';

interface FunctionRun {
  id: string;
  function_id: string;
  function_name: string;
  function_version: number;
  created_at: string;
  execution_time_ms: number;
  success: boolean;
  model_used: string;
  input_type: string;
  input_data?: any;
  output_data?: any;
  error?: string;
  ip_address?: string;
  user_agent?: string;
  metadata?: any;
  token_usage?: { prompt_tokens?: number; completion_tokens?: number; total_tokens?: number };
  total_tokens?: number;
  tokens?: number;
  ai_credits?: number;
  multiplier?: number;
}

interface FunctionDetails {
  id: string;
  name: string;
  display_name: string;
  function_name?: string;
  description?: string;
  is_public?: boolean;
  total_runs: number;
  successful_runs: number;
  failed_runs: number;
  average_execution_time: number;
}

export const FunctionRunDetails: React.FC = () => {
  const { functionId } = useParams<{ functionId: string }>();
  const navigate = useNavigate();
  const { user, permissions, getIdToken } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [functionDetails, setFunctionDetails] = useState<FunctionDetails | null>(null);
  const [runs, setRuns] = useState<FunctionRun[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [refreshing, setRefreshing] = useState(false);
  const [perfKey, setPerfKey] = useState(0);
  
  // Check if viewing another customer's data
  const customerId = searchParams.get('customer_id');
  const customerTag = searchParams.get('customer_tag');
  const isViewingOtherCustomer = isOtherCustomer(customerId, permissions);
  const { enabled: testsEnabled } = useTestsAccess(getIdToken, isViewingOtherCustomer ? customerId : null);
  const tab = tabParam === 'performance' ? 'performance' : tabParam === 'tests' && testsEnabled ? 'tests' : 'runs';
  const functionTitle = functionDetails ? functionDetails.display_name || functionDetails.name || functionDetails.function_name : null;
  const tabTitle = tab === 'performance' ? 'Performance' : tab === 'tests' ? 'Tests' : 'Runs';
  usePageTitle(functionTitle ? `${functionTitle} · ${tabTitle}` : null);

  useEffect(() => {
    if (functionId) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [functionId, page, rowsPerPage]);

  const loadData = async () => {
    if (!user || !functionId) return;

    try {
      setLoading(true);
      setError(null);
      const idToken = await user.getIdToken();

      // Build URLs based on whether viewing as admin
      const detailsUrl = isViewingOtherCustomer
        ? `/api/v2/analytics/admin/ai-functions/${functionId}?customer_id=${customerId}`
        : `/api/v2/analytics/ai-functions/${functionId}`;
        
      const runsUrl = isViewingOtherCustomer
        ? `/api/v2/analytics/admin/ai-functions/${functionId}/runs?customer_id=${customerId}&page=${page + 1}&page_size=${rowsPerPage}`
        : `/api/v2/analytics/ai-functions/${functionId}/runs?page=${page + 1}&page_size=${rowsPerPage}`;

      // Load function details
      const detailsResponse = await fetch(detailsUrl, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
        },
      });

      if (!detailsResponse.ok) {
        throw new Error('Failed to load function details');
      }

      const detailsData = await detailsResponse.json();
      setFunctionDetails(detailsData.data);

      // Load runs
      const runsResponse = await fetch(runsUrl, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
        },
      });

      if (!runsResponse.ok) {
        throw new Error('Failed to load function runs');
      }

      const runsData = await runsResponse.json();
      setRuns(runsData.results || runsData.data || []);
      setTotalCount(runsData.pagination?.total_items || 0);
    } catch (err) {

      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
  };

  const handleViewDetails = (run: FunctionRun) => {
    const params = new URLSearchParams();
    if (customerId) params.append('customer_id', customerId);
    if (customerTag) params.append('customer_tag', customerTag);
    const queryString = params.toString();
    navigate(`/dashboard/run/${run.id}${queryString ? '?' + queryString : ''}`, { state: { run } });
  };
  
  const listPath = () => {
    const params = new URLSearchParams();
    if (customerId) params.append('customer_id', customerId);
    if (customerTag) params.append('customer_tag', customerTag);
    const queryString = params.toString();
    return `/dashboard/function-runs${queryString ? '?' + queryString : ''}`;
  };

  const handleBack = () => navigate(listPath());

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  const getSuccessRate = () => {
    if (!functionDetails || functionDetails.total_runs === 0) return 0;
    return Math.round((functionDetails.successful_runs / functionDetails.total_runs) * 100);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !functionDetails) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || 'Function not found'}</Alert>
        <Button startIcon={<BackIcon />} onClick={handleBack} sx={{ mt: 2 }}>
          Go Back
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
        <IconButton onClick={handleBack}>
          <BackIcon />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <PageBreadcrumbs
            items={[
              {
                label: isViewingOtherCustomer ? `Runs & Performance · ${customerTag || customerId}` : 'Runs & Performance',
                to: listPath(),
              },
              { label: functionTitle || 'Function' },
            ]}
          />
          <Typography variant="h4">
            {functionTitle}
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {functionDetails.description || 'Function run history and performance metrics'}
          </Typography>
          {isViewingOtherCustomer && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Viewing runs for customer: {customerTag || customerId}
            </Typography>
          )}
        </Box>
        {functionId && !functionDetails.is_public && (
          <QuickLinks
            kind="function"
            id={functionId}
            name={functionDetails.name || functionDetails.function_name}
            editable={!isViewingOtherCustomer}
            onReliabilitySaved={() => setPerfKey(k => k + 1)}
          />
        )}
        <IconButton onClick={handleRefresh} disabled={refreshing}>
          <RefreshIcon />
        </IconButton>
      </Box>
      
      {isViewingOtherCustomer && (
        <Alert severity="info" sx={{ mb: 3 }}>
          You are viewing function runs for {customerTag ? `"${customerTag}"` : 'another customer'} as an admin.
        </Alert>
      )}

      {/* Summary Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Runs
              </Typography>
              <Typography variant="h4">
                {functionDetails.total_runs.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Success Rate
              </Typography>
              <Typography variant="h4">
                {getSuccessRate()}%
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {functionDetails.successful_runs.toLocaleString()} successful
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Average Time
              </Typography>
              <Typography variant="h4">
                {formatDuration(functionDetails.average_execution_time)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Failed Runs
              </Typography>
              <Typography variant="h4" color="error">
                {functionDetails.failed_runs.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {((functionDetails.failed_runs / functionDetails.total_runs) * 100).toFixed(1)}% failure rate
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Tabs
        value={tab}
        onChange={(_, v) => {
          const next = new URLSearchParams(searchParams);
          if (v === 'runs') next.delete('tab');
          else next.set('tab', v);
          setSearchParams(next, { replace: true });
        }}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="runs" label="Runs" />
        <Tab value="performance" label="Performance" />
        {testsEnabled && <Tab value="tests" label="Tests" />}
      </Tabs>

      {tab === 'performance' && functionId && (
        <FunctionPerformance key={perfKey} functionId={functionId} customerId={isViewingOtherCustomer ? customerId : null} />
      )}

      {tab === 'tests' && functionId && (
        <TargetTests targetType="function" targetId={functionId} customerId={isViewingOtherCustomer ? customerId : null} />
      )}

      {tab === 'runs' && (
      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Time</TableCell>
                <TableCell>Version</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Model</TableCell>
                <TableCell>Input</TableCell>
                <TableCell align="right">Tokens</TableCell>
                <TableCell align="right">AI Credits</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">View</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {runs.map((run) => {
                // Get tokens from the new simplified field, with fallbacks
                const tokens = run.tokens ?? run.total_tokens ?? 
                              run.token_usage?.total_tokens ?? 
                              (run.token_usage ? ((run.token_usage.prompt_tokens || 0) + (run.token_usage.completion_tokens || 0)) : 0);
                const aiCredits = run.ai_credits ?? 0;
                return (
                  <TableRow key={run.id} hover>
                    <TableCell><RelativeTime value={run.created_at} /></TableCell>
                    <TableCell>{run.function_version}</TableCell>
                    <TableCell>{formatDuration(run.execution_time_ms)}</TableCell>
                    <TableCell>{run.model_used}</TableCell>
                    <TableCell>{run.input_type}</TableCell>
                    <TableCell align="right">{tokens ? tokens.toLocaleString() : '-'}</TableCell>
                    <TableCell align="right">{aiCredits ? aiCredits.toFixed(2) : '-'}</TableCell>
                    <TableCell align="center">
                      {run.success ? (
                        <Chip icon={<SuccessIcon />} label="Success" color="success" size="small" />
                      ) : (
                        <Tooltip title={run.error || 'Failed'}>
                          <Chip icon={<ErrorIcon />} label="Failed" color="error" size="small" />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View full run details">
                        <IconButton size="small" onClick={() => handleViewDetails(run)}>
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        />
      </Paper>
      )}

    </Box>
  );
};

